import type { VStackProps } from '@frontend/legacy-components';

import {
  DisplayWrapper,
  DisplayTitle,
  DisplayEntry,
  DisplayEntryWrapper,
} from 'modules/common/viewer/components/ViewerDisplayElements';

import type { DxfSelectionData } from '../hooks/useDxfDetailsOverlay';

export type DxfDetailsOverlayProps = {
  /** Overall drawing size, already formatted, or `null` to leave it out. */
  size: string | null;
  /** Whether a selection (or hovered layer) contributes detail rows. */
  hasDetail: boolean;
  /** Title over the detail rows — "Cumulative Selection" or the hovered layer's name. */
  heading: string;
  /** Number of items on a hovered layer, or `null` to leave it out. */
  count: number | null;
  /** Total path length, already formatted. */
  length: string;
  /**
   * Enclosed area when a closed loop contributes one; `null` when an open chain
   * or edge chunk encloses nothing — a zero there would be a lie.
   */
  area: string | null;
  /** The last pick's type and length, or `null` when there is no single last pick. */
  selectionData: DxfSelectionData | null;
} & VStackProps;

/**
 * Bottom-right readout of what the drawing is and what is picked.
 *
 * Pure and controlled: reports the part's overall size when given one; the
 * total path length when something is picked or a layer is hovered, with the
 * item count and enclosed area only when given; then the last pick's own type
 * and length, and its diameter and angle when it lies on one circle, as the
 * legacy viewer's "Selection Data" does. Kept free of the session so it stays
 * trivially testable — `useDxfDetailsOverlay` is the piece that subscribes, and
 * decides which rows apply.
 */
export const DxfDetailsOverlay = ({
  size,
  hasDetail,
  heading,
  count,
  length,
  area,
  selectionData,
  ...props
}: DxfDetailsOverlayProps) => (
  <DisplayWrapper
    color="white"
    pointerEvents="none"
    data-testid="dxf-details-overlay"
    {...props}
  >
    {size !== null && (
      <DisplayEntryWrapper>
        <DisplayEntry>Size:</DisplayEntry>
        <DisplayEntry>{size}</DisplayEntry>
      </DisplayEntryWrapper>
    )}
    {hasDetail && (
      <>
        <DisplayTitle>{heading}</DisplayTitle>
        {count !== null && (
          <DisplayEntryWrapper>
            <DisplayEntry>Count:</DisplayEntry>
            <DisplayEntry>{`${count}`}</DisplayEntry>
          </DisplayEntryWrapper>
        )}
        <DisplayEntryWrapper>
          <DisplayEntry>Edge Length:</DisplayEntry>
          <DisplayEntry>{length}</DisplayEntry>
        </DisplayEntryWrapper>
        {area !== null && (
          <DisplayEntryWrapper>
            <DisplayEntry>Area:</DisplayEntry>
            <DisplayEntry>{area}</DisplayEntry>
          </DisplayEntryWrapper>
        )}
      </>
    )}
    {selectionData && (
      <>
        <DisplayTitle>Selection Data</DisplayTitle>
        {selectionData.type !== null && (
          <DisplayEntryWrapper>
            <DisplayEntry>Type:</DisplayEntry>
            <DisplayEntry>{selectionData.type}</DisplayEntry>
          </DisplayEntryWrapper>
        )}
        <DisplayEntryWrapper>
          <DisplayEntry>Length:</DisplayEntry>
          <DisplayEntry>{selectionData.length}</DisplayEntry>
        </DisplayEntryWrapper>
        {selectionData.arc && (
          <>
            <DisplayEntryWrapper>
              <DisplayEntry>Diameter:</DisplayEntry>
              <DisplayEntry>{selectionData.arc.diameter}</DisplayEntry>
            </DisplayEntryWrapper>
            <DisplayEntryWrapper>
              <DisplayEntry>Angle:</DisplayEntry>
              <DisplayEntry>{selectionData.arc.angle}</DisplayEntry>
            </DisplayEntryWrapper>
          </>
        )}
      </>
    )}
  </DisplayWrapper>
);
DxfDetailsOverlay.displayName = 'DxfDetailsOverlay';
